import React, {useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import TryAgain from "../../../../../components/app-specific/try-again";
import {Col, Row} from "reactstrap";
import CompareViewPartsAutoComplete from "./sections/auto-complete";
import classnames from "classnames";
import Paper from "@mui/material/Paper";
import {Close} from "@mui/icons-material";
import Api from "../../../../../../core/services/api/api";
import IconButton from "../../../../../components/base/button/icon_button";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import CompareViewTableSection from "./sections/table";
import CompareViewImagesSection from "./sections/image-comparison";
import CompareViewNotesSection from "./sections/notes-comparison";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useIndustry from "../../../../../hooks/use-industry";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import {Box, Fade} from "@mui/material";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const CompareViewTabs = {
    detailComparison: 'detail-comparison',
    imageComparison: 'image-comparison',
    notesComparison: 'notes-comparison',
}
const CompareListView = () => {
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('lookup-compare-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const categoryIndustryCache = useRef({});
    const {industries, loadingIndustries, hasNoIndustries} = useIndustry({noCategories: true});

    const selectedData = useMemo(() => data.find(e => e.category.id === selectedCategoryId), [selectedCategoryId, data])
    const parts = useMemo(() => selectedData?.parts ?? [], [selectedData])
    const allParts = useMemo(() => data?.map(e => e?.parts ?? [])?.reduce((p, c) => [...p, ...c], []) ?? [], [data])
    const [tab, setTab] = useState(CompareViewTabs.detailComparison);

    const tabs = useMemo(() => Object.values(CompareViewTabs).map(e => ({
        value: e,
        name: (function () {
            switch (e) {
                case CompareViewTabs.detailComparison:
                    return "Detail Comparison";
                case CompareViewTabs.imageComparison:
                    return "Image Comparison";
                case CompareViewTabs.notesComparison:
                    return "Note Comparison";
                default:
                    return "";
            }
        })(),
    })), []);


    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.lookupCompareList);
        configureBehavioralTool((tags) => tags.lookupCompareList);
    }, [configureBehavioralTool, configureNotebookTags]);


    /**
     * As soon as the component mounts:
     * - fetches the comparison lists items from the server.
     * - sets the url name for potential tasks to be created
     */
    useEffect(() => {
        if (!loadingIndustries) {
            getComparisonItems(true).then()
        }
    }, [loadingIndustries])

    const fetchIndustriesOfCategories = async (categoryIds) => {
        const unknownCategoryIds = categoryIds.filter(e => !categoryIndustryCache.current[e]);
        if (unknownCategoryIds.length) {
            const response = await Api.getCategoryIndustryIds({
                ids: unknownCategoryIds,
            })
            if (!isMounted())
                return;
            if (response?.resultFlag) {
                categoryIndustryCache.current = {
                    ...categoryIndustryCache.current,
                    ...response.data.reduce((p, c) => ({...p, [c.id]: industries.find(e => e.id === c.industryId)}), {})
                }
            }
        }
    }

    /**
     * Fetches the comparison items list from the server.
     */
    const getComparisonItems = async (initial = false) => {
        const response = await Api.getComparisonList();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            await fetchIndustriesOfCategories(response?.data?.map(e => e.category.id) ?? []);
            if (!isMounted())
                return;
            const data = response?.data?.map(d => ({
                ...d,
                category: {
                    ...d.category,
                    industry: categoryIndustryCache.current[d.category.id],
                },
                parts: d?.parts?.map(e => ({
                    ...e,
                    coverImageURL: response?.configuration?.PartImageURL?.concat(e?.coverImageURL ?? '') ?? '',
                    images: e?.images
                        ?.sort((a, b) => a.orderNumber - b.orderNumber)
                        ?.map(e => ({
                            ...e,
                            fileName: response?.configuration?.PartImageURL?.concat(e?.fileName ?? '') ?? ''
                        })),
                    pinned: false,
                })) ?? [],
            })) ?? []
            setData(data);
            if (data?.length) {
                const notFound = !data?.find(e => e.category?.id === selectedCategoryId);
                if (initial || notFound) {
                    setSelectedCategoryId(data[0].category.id);
                }
            }
        }
        setLoading(false);
    };

    /**
     * Removes the given category and all of its parts from the comparison list in the server.
     * @param {any} category
     * @return {Promise<void>}
     */
    const removeCategory = async (category) => {
        const response = await Api.removeCategoryFromCompareList(category.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => {
                const newState = prevState.filter(e => e.category.id !== category.id)
                if (category.id === selectedCategoryId) {
                    setSelectedCategoryId(newState.length ? newState[0].category.id : null);
                }
                return newState;
            });
        }
    }

    /**
     * Removes the given part from the comparison list in the server.
     * @param {any} part
     * @return {Promise<void>}
     */
    const removePart = async (part) => {
        const response = await Api.removePartFromCompareList(part.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => {
                const newData = prevState
                    ?.map(e => {
                        if (e.category.id !== selectedCategoryId)
                            return e;
                        const newParts = e.parts?.filter(e => e.id !== part.id) ?? [];
                        if (!newParts?.length) {
                            // remove the empty category
                            return null;
                        }
                        return {
                            ...e,
                            parts: newParts
                        }
                    })
                    ?.filter(e => e)
                const category = newData?.find(e => e.category.id === selectedCategoryId);
                if (!category && !!selectedCategoryId && newData?.length) {
                    setSelectedCategoryId(newData[0].category.id);
                }
                return newData;

            });

        }
    }

    /**
     * Handles selection of a category for showing the selected items:
     * - Sets the [selectedCategoryId] in the state with the received category
     *
     * @param {Object} category
     */
    const onCategorySelected = (category) => {
        if (selectedCategoryId === category.id)
            return;
        setSelectedCategoryId(category.id);
    }

    /**
     * Handles removing a category from the list of items in compare screen:
     * - Shows a confirmation dialog from for user
     * - after confirmation, calls the api regarding this action
     *
     * @param {MouseEvent<HTMLButtonElement>} e
     * @param {Object}  category
     */
    const getRemoveCategoryConfirmation = (e, category) => {
        e.stopPropagation()
        e.preventDefault()
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    By removing this category, all of its parts will be removed from the comparison list. Do you want to
                    proceed?
                </p>
            </div>,
            {},
            (decision) => decision && removeCategory(category).then(),
            "Confirm",
            "Cancel",
        );
    }

    /**
     * Adds the given part to the appropriate category form list of categories of the state and also selects the
     * category that the part belongs to.
     */
    const onPartAdded = async (part) => {
        await getComparisonItems(false);
        setData(prevState => {
            const category = prevState?.find(e => e?.parts?.find(e => e.id === part.id))?.category;
            if (category)
                setSelectedCategoryId(category.id);
            return prevState;
        })
    }

    const onExistingPartSelected = (part) => {
        const category = data?.find(e => e?.parts?.find(e => e.id === part.id))?.category;
        if (category)
            setSelectedCategoryId(category.id);
    }

    /**
     * Toggles the pinned state of the given part.
     * @param part
     */
    const togglePinned = (part) => {
        setData(prevState => prevState?.map(e => e.category.id === selectedCategoryId
            ? {
                ...e,
                parts: e.parts?.map(e => e.id === part.id
                    ? {...e, pinned: !e.pinned}
                    : e
                ) ?? []
            }
            : e
        ))
    }

    /**
     * Handles changes in the selected tab and sets the tab index to the received [newValue] in the props
     * @param event
     * @param newValue
     */
    const onTabChange = (event, newValue) => {
        setTab(newValue);
    }

    return (
        <>
            {
                loadingIndustries &&
                <LoadingLottieAnimation width={'60%'}/>
            }
            {
                !loadingIndustries &&
                <div className={'new-compare-view'}>
                    <Row>
                        <Col xs={12}>
                            <div className={'category-list'}>
                                {
                                    data.map(({category}) => (
                                        <div
                                            key={category?.id}
                                            className={classnames('category-card', {'selected': category.id === selectedCategoryId})}
                                            onClick={() => onCategorySelected(category)}
                                        >
                                            <p className={'mb-0'}>
                                                {(category?.title ?? "").concat(
                                                    (category?.industryTitle || category?.industry?.title)
                                                        ? ` (${category?.industryTitle ?? category?.industry?.title})`
                                                        : ""
                                                )}
                                            </p>
                                            <IconButton
                                                color={"paper.error"}
                                                className={'remove-category-from-compare'}
                                                onClick={(e) => getRemoveCategoryConfirmation(e, category)}
                                            >
                                                <Close className='icon small remove-category'/>
                                            </IconButton>
                                        </div>
                                    ))
                                }
                            </div>
                        </Col>
                        <Col xs={12}>
                            <div className={'compare-part-addition-container'}>
                                <p className={'mb-2'}>
                                    Add Part
                                </p>
                                <CompareViewPartsAutoComplete
                                    onPartAdded={onPartAdded}
                                    onExistingPartSelected={onExistingPartSelected}
                                    currentlySelectedParts={allParts}
                                    selectedCategoryId={selectedCategoryId}
                                />
                            </div>
                        </Col>
                    </Row>
                    {
                        loading
                            ? <div className={'loading comparison-table'}>
                                <div/>
                            </div>
                            : !data?.length
                                ? <TryAgain
                                    text={'Your comparison list is empty'}
                                />
                                : <Fade in={!!selectedData}>
                                    <Box component={'span'}>
                                        <Tabs value={tab ?? false} onChange={onTabChange}>
                                            {
                                                tabs.map(({value, name}) => (
                                                    <Tab key={value} label={name} value={value}/>
                                                ))
                                            }
                                        </Tabs>
                                        {
                                            tab === CompareViewTabs.detailComparison &&
                                            <Paper className={'p-2 mt-4'} elevation={0}>
                                                {
                                                    hasNoIndustries &&
                                                    <p className={'text-sm grey-3 font-weight-semi-bold mb-3 text-center'}>
                                                        No industries found. Please add an industry or contact your administrator.
                                                    </p>
                                                }
                                                {
                                                    !hasNoIndustries &&
                                                    <CompareViewTableSection
                                                        selectedCategory={selectedData?.category}
                                                        parts={parts}
                                                        togglePinned={togglePinned}
                                                        removePart={removePart}
                                                    />
                                                }
                                            </Paper>
                                        }
                                        {
                                            tab === CompareViewTabs.imageComparison &&
                                            <CompareViewImagesSection
                                                parts={parts}
                                                removePart={removePart}
                                            />
                                        }
                                        {
                                            tab === CompareViewTabs.notesComparison &&
                                            <CompareViewNotesSection
                                                parts={parts}
                                                removePart={removePart}
                                            />
                                        }
                                    </Box>
                                </Fade>
                    }
                </div>
            }

        </>

    );
}

export default CompareListView;
